import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@common/lib/utils';

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}
function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  // allow passing a container to render inline
  container?: HTMLElement | null;
  // when true, content is rendered in-place (no portal) so it stays inside parent DOM (e.g. modal/sidebar)
  disablePortal?: boolean;
};

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({
 className, align = 'center', sideOffset = 4, container, disablePortal, ...props
}, ref) => {
  const content = (
    <PopoverPrimitive.Content
      data-slot="popover-content"
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        `tw-bg-popover tw-text-popover-foreground tw-data-[state=open]:tw-animate-in tw-data-[state=closed]:tw-animate-out
        tw-data-[state=closed]:tw-fade-out-0 tw-data-[state=open]:tw-fade-in-0 tw-data-[state=closed]:tw-zoom-out-95
        tw-data-[state=open]:tw-zoom-in-95 tw-data-[side=bottom]:tw-slide-in-from-top-2
        tw-data-[side=left]:tw-slide-in-from-right-2 tw-data-[side=right]:tw-slide-in-from-left-2
        tw-data-[side=top]:tw-slide-in-from-bottom-2 tw-z-50 tw-w-72 tw-origin-[var(--radix-popover-content-transform-origin)]
        tw-rounded-md tw-border tw-border-border tw-p-4 tw-shadow-md tw-outline-none`,
        className
      )}
      {...(props as any)}
    />
  );

  // If a container is provided or portal disabled, render Content inline (no portal) so it stays inside parent.
  if (container || disablePortal) {
    return content;
  }

  // otherwise use the default portal behavior
  return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent };
